import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ErrorHandler, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { STORAGE_KEYS } from '../../shared/constants/storage-keys';

@Injectable()
export class ChunkLoadErrorHandler extends ErrorHandler {
  private readonly chunkLoadErrorPatterns = [
    'chunkloaderror',
    'loading chunk',
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'importing a module script failed',
  ] as const;

  private readonly reloadAttemptedFlag = 'true';
  private readonly cacheBustQueryParam = 'chunk-reload';

  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  public override handleError(error: unknown): void {
    if (this.isChunkLoadError(error) && this.reloadForFreshAssets()) {
      return;
    }

    super.handleError(error);
  }

  private isChunkLoadError(error: unknown): boolean {
    const errorText = this.buildErrorText(this.resolveErrorCandidate(error));
    return this.matchesChunkLoadPattern(errorText);
  }

  private resolveErrorCandidate(error: unknown): unknown {
    const record = this.toRecord(error);
    return record?.['rejection'] ?? record?.['reason'] ?? record?.['error'] ?? error;
  }

  private buildErrorText(candidate: unknown): string {
    const record = this.toRecord(candidate);
    const name = record ? String(record['name'] ?? '') : '';
    const message = record ? String(record['message'] ?? '') : String(candidate ?? '');
    return `${name} ${message}`.toLowerCase();
  }

  private matchesChunkLoadPattern(errorText: string): boolean {
    return this.chunkLoadErrorPatterns.some((pattern) => errorText.includes(pattern));
  }

  private reloadForFreshAssets(): boolean {
    const browserWindow = this.getBrowserWindow();
    if (!browserWindow) {
      return false;
    }

    if (this.hasReloadAlreadyBeenAttempted(browserWindow)) {
      return false;
    }

    this.markReloadAttempted(browserWindow);
    void this.clearBrowserCaches(browserWindow).finally(() => {
      this.reloadWithCacheBust(browserWindow);
    });

    return true;
  }

  private getBrowserWindow(): Window | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const browserWindow = this.document.defaultView;
    if (!browserWindow?.sessionStorage) {
      return null;
    }

    return browserWindow;
  }

  private hasReloadAlreadyBeenAttempted(browserWindow: Window): boolean {
    return (
      browserWindow.sessionStorage.getItem(STORAGE_KEYS.CHUNK_LOAD_RELOAD_ATTEMPTED) ===
      this.reloadAttemptedFlag
    );
  }

  private markReloadAttempted(browserWindow: Window): void {
    browserWindow.sessionStorage.setItem(
      STORAGE_KEYS.CHUNK_LOAD_RELOAD_ATTEMPTED,
      this.reloadAttemptedFlag,
    );
  }

  private async clearBrowserCaches(browserWindow: Window): Promise<void> {
    if (!('caches' in browserWindow)) {
      return;
    }

    const cacheNames = await browserWindow.caches.keys();
    await Promise.all(cacheNames.map((cacheName) => browserWindow.caches.delete(cacheName)));
  }

  private reloadWithCacheBust(browserWindow: Window): void {
    const url = new URL(browserWindow.location.href);
    url.searchParams.set(this.cacheBustQueryParam, Date.now().toString());
    browserWindow.location.replace(url.toString());
  }

  private toRecord(value: unknown): Record<string, unknown> | null {
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
  }
}
